import { TRIBES } from "@/lib/tribes";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Small circular tribe identity marker. Meant to appear ONCE per screen —
 * next to the child's name in the ChildHome header, or in the corner of a
 * child card — not as a decorative background or repeated pattern.
 *
 * `child` prop (optional) — when provided and the child is on tribe=thrive_story
 * with a published companion photo, the emblem swaps in that photo at the same
 * size/ring treatment. This is how a family's "adventure companion" becomes
 * their equivalent of the Thrive Tribe star / Myo Princess crown.
 */
export default function TribeEmblem({ tribeKey, child, size = 40, className = "", testid, showRing = true }) {
  // Prefer the full child object when passed; otherwise fall back to tribeKey alone.
  const effectiveTribe = child?.tribe || tribeKey;
  const tribe = TRIBES[effectiveTribe] || TRIBES.neutral;
  if (effectiveTribe === "neutral" || !effectiveTribe) return null;

  // Thrive Story: if the admin has uploaded the family's adventure companion
  // AND published the story, use the companion photo in place of the emblem.
  const isCustom =
    effectiveTribe === "thrive_story" &&
    child?.story_assets_ready &&
    child?.companion_photo_path;
  const customSrc = isCustom ? `${BACKEND_URL}/api/public-files/${child.companion_photo_path}` : null;

  // At small sizes (<80px) tribes render a single-symbol emblem (crown /
  // shuriken / star) that reads clearly on tiny avatars and list rows.
  // At 80px+ tribes with an `emblem_svg_large` swap in the full illustrated
  // artwork (e.g. the Myo Princess character badge on certificates).
  const useLarge = size >= 80 && tribe.emblem_svg_large;
  const src = customSrc || (useLarge ? tribe.emblem_svg_large : tribe.emblem_svg);
  // Companion photos look best on a transparent/neutral disc so the artwork reads clean.
  const bg = isCustom ? "#FFFEF9" : (useLarge ? (tribe.emblem_bg_large || "transparent") : tribe.emblem_bg);
  const ringColor = isCustom ? (child?.favorite_color || tribe.accent) : bg;
  return (
    <div
      data-testid={testid || `tribe-emblem-${tribe.key}`}
      className={`inline-flex items-center justify-center shrink-0 rounded-full overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        background: bg === "transparent" ? "transparent" : bg,
        boxShadow: showRing && ringColor && ringColor !== "transparent" ? `0 0 0 2px #FFFEF9, 0 0 0 3px ${ringColor}` : "none",
      }}
      aria-label={`${tribe.name} badge`}
      title={tribe.name}
    >
      <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: isCustom ? "cover" : "contain" }} draggable={false} />
    </div>
  );
}
